import { supabase } from '../lib/supabase';

// Resolve image URL: if it's a full URL return as-is, otherwise treat as Supabase storage path
const MARKETPLACE_BUCKET = 'marketplace-images';

const resolveImageUrl = (url) => {
  if (!url) return null;
  // Already a full URL (http/https)
  if (url?.startsWith('http://') || url?.startsWith('https://')) {
    return url;
  }
  // Treat as a Supabase storage path
  const { data } = supabase?.storage?.from(MARKETPLACE_BUCKET)?.getPublicUrl(url);
  return data?.publicUrl || null;
};

export const marketplaceService = {
  // Get all unsold marketplace items with filters
  async getItems(filters = {}) {
    try {
      let query = supabase
        ?.from('marketplace_items')
        ?.select(`
          *,
          seller:seller_id (
            id,
            full_name,
            avatar_url
          )
        `)
        ?.eq('is_sold', false)
        ?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.search) {
        query = query?.or(`title.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%`);
      }

      if (filters?.condition) {
        query = query?.eq('condition', filters?.condition);
      }

      if (filters?.location) {
        query = query?.ilike('location', `%${filters?.location}%`);
      }

      if (filters?.minPrice) {
        query = query?.gte('price', parseFloat(filters?.minPrice));
      }

      if (filters?.maxPrice) {
        query = query?.lte('price', parseFloat(filters?.maxPrice));
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform data to match UI expectations
      return (data || [])?.map(item => {
        const rawImages = item?.images || [];
        const resolvedImages = rawImages?.map(resolveImageUrl)?.filter(Boolean);
        const firstImage = resolvedImages?.[0] || null;

        return {
          id: item?.id,
          seller_id: item?.seller_id,
          title: item?.title,
          description: item?.description,
          price: item?.price,
          condition: item?.condition,
          location: item?.location,
          contact_info: item?.contact_info,
          is_sold: item?.is_sold,
          created_at: item?.created_at,
          image: firstImage,
          imageAlt: `${item?.title} görsel`,
          sellerName: item?.seller?.full_name || 'İsimsiz Kullanıcı',
          sellerAvatar: item?.seller?.avatar_url || null,
          sellerAvatarAlt: `${item?.seller?.full_name || 'Kullanıcı'} profil fotoğrafı`,
          postedDate: new Date(item?.created_at)?.toLocaleDateString('tr-TR')
        };
      });
    } catch (error) {
      console.error('Error fetching marketplace items:', error);
      throw error;
    }
  },

  // Get marketplace item by ID
  async getItemById(itemId) {
    try {
      const { data, error } = await supabase
        ?.from('marketplace_items')
        ?.select(`
          *,
          seller:seller_id (
            id,
            full_name,
            avatar_url,
            email,
            phone,
            location,
            created_at
          )
        `)
        ?.eq('id', itemId)
        ?.single();

      if (error) throw error;

      if (!data) return null;

      const rawImages = data?.images || [];
      const resolvedImages = rawImages?.map(resolveImageUrl)?.filter(Boolean);
      const firstImage = resolvedImages?.[0] || null;

      // Transform data to match UI expectations
      return {
        id: data?.id,
        seller_id: data?.seller_id,
        title: data?.title,
        description: data?.description,
        price: data?.price,
        condition: data?.condition,
        location: data?.location,
        contact_info: data?.contact_info,
        is_sold: data?.is_sold,
        status: data?.status,
        created_at: data?.created_at,
        images: resolvedImages,
        image: firstImage,
        imageAlt: `${data?.title} görsel`,
        seller: {
          id: data?.seller?.id,
          name: data?.seller?.full_name || 'İsimsiz Kullanıcı',
          avatar: data?.seller?.avatar_url || null,
          avatarAlt: `${data?.seller?.full_name || 'Kullanıcı'} profil fotoğrafı`,
          email: data?.seller?.email,
          phone: data?.seller?.phone,
          location: data?.seller?.location,
          memberSince: data?.seller?.created_at
        },
        postedDate: new Date(data?.created_at)?.toLocaleDateString('tr-TR')
      };
    } catch (error) {
      console.error('Error fetching marketplace item:', error);
      throw error;
    }
  },

  // Create new marketplace listing
  async createItem(itemData, imageUrls) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('marketplace_items')?.insert({
          seller_id: user?.id,
          title: itemData?.title,
          description: itemData?.description,
          price: parseFloat(itemData?.price),
          condition: itemData?.condition,
          location: itemData?.location,
          images: imageUrls,
          contact_info: itemData?.contactInfo || user?.email,
          is_sold: false,
          status: 'pending'
        })?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error creating marketplace item:', error);
      throw error;
    }
  },

  // Update existing marketplace listing (edit mode)
  async updateItem(itemId, itemData, imageUrls) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('marketplace_items')
        ?.update({
          title: itemData?.title,
          description: itemData?.description,
          price: parseFloat(itemData?.price),
          condition: itemData?.condition,
          location: itemData?.location,
          images: imageUrls,
          contact_info: itemData?.contactInfo,
        })
        ?.eq('id', itemId)
        ?.eq('seller_id', user?.id)
        ?.select()
        ?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error updating marketplace item:', error);
      throw error;
    }
  },

  // Mark item as sold
  async markAsSold(itemId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('marketplace_items')?.update({ is_sold: true })?.eq('id', itemId)?.eq('seller_id', user?.id)?.select()?.single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error marking item as sold:', error);
      throw error;
    }
  },

  // Delete marketplace item
  async deleteItem(itemId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('marketplace_items')?.delete()?.eq('id', itemId)?.eq('seller_id', user?.id);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting marketplace item:', error);
      throw error;
    }
  },

  // Get user's own listings
  async getUserListings() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('marketplace_items')?.select('*')?.eq('seller_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching user listings:', error);
      throw error;
    }
  },

  // Update listing status (Admin moderation)
  async updateListingStatus(itemId, newStatus) {
    try {
      console.log('Updating listing status:', { itemId, newStatus });

      const { data, error } = await supabase
        ?.from('marketplace_items')
        ?.update({ status: newStatus })
        ?.eq('id', itemId)
        ?.select(); // Remove .single() to prevent PGRST116 error

      if (error) throw error;

      // Return first item safely, or null if no rows returned
      const updatedItem = data?.[0] || null;
      console.log('Listing status updated successfully:', updatedItem);
      return updatedItem;
    } catch (error) {
      console.error('Error updating listing status:', error);
      throw error;
    }
  }
};